import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaTimes } from 'react-icons/fa';
import axios from 'axios';
import { useSelector } from 'react-redux';
import { Document, Page, pdfjs } from 'react-pdf';
import downloadIcon from '../../images/consultations/download-dark.svg';
import LoadingComponent from '../../components/common/LoadingComponent';
import StyledTappable from '../../components/common/StyledTappable';
import backButton from '../../images/pharmacy/back.svg';
import Tappable from 'react-tappable/lib/Tappable';

const OuterContainer = styled.div`
  background: #fff;
  height: 100svh;
  width: 100vw;
  position: absolute;
  z-index: 100;
  bottom: 0;
  padding: 12px 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 440px;
`;
const Header = styled.div`
  font-family: 14px;
  font-weight: 500;
  width: 100%;
  padding: 10px;
  padding-top: 0px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #f0f3f6;
`;
const CloseContainer = styled(StyledTappable)`
  width: 1.625rem;
  height: 1.625rem;
  border-radius: 8px;
  border: 1px solid #eee;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const CloseIcon = styled(FaTimes)`
  color: #413958;
`;
const Btn = styled.div`
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #f0f0f0;
  width: fit-content;
  font-size: 14px;
  height: 24px;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 16px;
  text-align: center;
  cursor: pointer;
  transition: all 0.1s ease;
  &:hover {
    opacity: 0.6;
  }
  &:active {
    opacity: 0.3;
  }
`;
const FileContainer = styled.div`
  width: 100%;
  height: 100%;
  max-width: 420px;
  overflow: auto; /* Make the container scrollable */
`;
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 4px;
  align-items: center;
  padding: 4px;
  background: #ddd;
  min-height: 500px;
  width: 100%;
  max-width: 420px;
  padding-bottom: 80px;
`;
const BackContainer = styled(Tappable)`
  margin-right: 21px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  &.Tappable-active {
    opacity: 0.2;
  }
`;

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const FileRender = ({ pdfUrl, pdfData, numPages, setNumPages }) => {
  useEffect(() => {
    return () => {
      URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  return (
    <div className="h-full">
      <FileContainer>
        <Document
          file={pdfData}
          onLoadSuccess={onDocumentLoadSuccess}
          renderTextLayer={false} // Disable text layer
          renderAnnotationLayer={false} // Disable annotation layer
          width={420}
        >
          <PageContainer>
            {[...Array(numPages)].map((_, index) => (
              <Page
                zoom={1}
                width={'400'}
                key={`page_${index + 1}`}
                pageNumber={index + 1}
                style={{ width: '100vw' }}
              />
            ))}
          </PageContainer>
        </Document>
      </FileContainer>
    </div>
  );
};

const FileViewerPage = (props) => {
  const searchParams = new URLSearchParams(props.location.search);
  const contentUrl = searchParams?.get('contentUrl') || ''; // Provide a default value
  const headerTitle = searchParams?.get('headerTitle') || 'PDF Viewer'; // Provide a default value
  const downloadable = searchParams?.get('downloadable') || 'true'; // Provide a default value

  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState(null);
  const [errorText, setErrorText] = useState('');
  const authToken = useSelector((state) => state.user.authToken);
  const [data, setData] = useState({ buffer: null, type: null });
  const [numPages, setNumPages] = useState(null);

  const downloadPdf = () => {
    if (typeof window.Android !== 'undefined') {
      window.Android.downloadPdf(contentUrl);
    } else if (window.ReactNativeWebView) {
    } else if (window.webkit && window.webkit.messageHandlers.visitIosView) {
    } else {
      const link = document.getElementById('prescription-download');
      link.click();
    }
  };

  const fetchPDF = async () => {
    setError(false);
    setFetching(true);
    setErrorText('');
    try {
      const response = await axios.get(contentUrl, {
        headers: {
          Authorization: authToken,
          'Content-Type': 'application/json',
        },
        responseType: 'arraybuffer',
      });
      const mimeType = response.headers['content-type'];
      const uint8Array = new Uint8Array(response.data);
      const pdfBlob = new Blob([uint8Array], { type: mimeType });
      setData({
        buffer: pdfBlob,
        type: mimeType,
      });
      setError(false);
      setErrorText('');
    } catch (error) {
      console.error('Error fetching PDF:', error);
      setError(true);
      setErrorText(error.message);
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => {
    fetchPDF();
  }, [contentUrl]);

  function handleClose() {
    setData({ buffer: null, type: null });
    if (typeof window.Android !== 'undefined') {
      window.Android.closeView();
      window.Android.closeView(true);
    } else if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({
          method: 'closeView',
        })
      );
    } else if (window.webkit && window.webkit.messageHandlers.visitIosView) {
      window.webkit.messageHandlers.visitIosView.postMessage(
        JSON.stringify({
          method: 'closeView',
        })
      );
    } else {
      props.history.goBack();
    }
  }

  return (
    <OuterContainer>
      <Header>
        <div className="flex flex-row items-center justify-between w-full">
          <div className="flex items-center gap-4">
            <BackContainer onTap={handleClose}>
              <img src={backButton} alt="" />
            </BackContainer>
            <p className=" text-body-l text-txtlight-new font-semibold">
              {headerTitle}
            </p>
          </div>
          {!fetching && !error && downloadable === 'true' && (
            <Btn onClick={downloadPdf}>
              <img
                src={downloadIcon}
                alt="download-icon"
                style={{ height: '12px' }}
              />
              <span>Download</span>
            </Btn>
          )}
        </div>
      </Header>
      {fetching && !error && <LoadingComponent />}
      {error && (
        <div className="text-body-l mt-4">
          Some Error occurred in fetching prescription:
          <p className="text-body-l">{errorText}</p>
        </div>
      )}
      {data?.buffer && (
        <a
          id="prescription-download"
          download={'visit-prescription.pdf'}
          href={URL.createObjectURL(data?.buffer)}
          className="hidden"
        />
      )}
      {data?.buffer && data?.type === 'application/pdf' ? (
        <FileRender
          pdfData={data.buffer}
          pdfUrl={contentUrl}
          setNumPages={setNumPages}
          numPages={numPages}
        />
      ) : (
        data?.buffer && <img src={URL?.createObjectURL(data?.buffer)} alt="" />
      )}
    </OuterContainer>
  );
};

export default FileViewerPage;
